/**
 * Shared data formatting utilities for Service Request Portal integration.
 * Used by both the CreateServiceRequestModal (legacy iframe path)
 * and the CreateServiceRequest full-page component (module federation path).
 */

/**
 * Transform patient details to only include name and address.
 * Country values are normalised to ISO 3166-1 alpha-2 codes so the SR Portal
 * can use them for validation, API calls, and map search — no fallback to the
 * clinic country to avoid silently assigning the wrong country.
 */
export const formatPatientDetailsForSR = (patientDetails) => {
  if (!patientDetails) return null;

  return {
    patientId: patientDetails.accountId?.value || '',
    firstName: patientDetails.firstName?.value || '',
    lastName: patientDetails.lastName?.value || '',
    phone: patientDetails.phoneNumber?.value || '',
    email: patientDetails.email?.value || '',
    dateOfBirth: patientDetails.dateOfBirth?.value || '',
    isUnderAge: patientDetails.isUnderAge?.value || false,
    address: {
      id: patientDetails.addressId?.value || '',
      street1: patientDetails.street1?.value || '',
      street2: patientDetails.street2?.value || '',
      city: patientDetails.city?.value || '',
      state: patientDetails.state?.value || '',
      postcode: patientDetails.postcode?.value || '',
      country: patientDetails.countryCode?.value || '',
      countryName: patientDetails.country?.value || '',
    },
    ...(patientDetails.shippingAddress?.value && {
      shippingAddress: {
        ...patientDetails.shippingAddress.value,
        country:
          patientDetails.shippingAddress.value.countryCode || '',
        countryName:
          patientDetails.shippingAddress.value.country || '',
      },
    }),
    ...(patientDetails.billingAddress?.value && {
      billingAddress: {
        ...patientDetails.billingAddress.value,
        country:
          patientDetails.billingAddress.value.countryCode || '',
        countryName:
          patientDetails.billingAddress.value.country || '',
      },
    }),
  };
};

/**
 * Transform clinic details from nested value objects to simple array.
 *
 * @param {object} currentProvider - The requesting clinic from providerService.getCurrentProvider().
 *   Its address.street may be an array (APAC Boomi response) or a string.
 * @param {object} [associatedClinicData] - All patient-associated clinics from
 *   associatedClinicDataRef.current, shaped as { values: [{ clinicId, clinicName, street1, ... }] }.
 *   Each field is a nested value object (e.g. clinicId.value).
 */
export const formatClinicDetailsForSR = (
  currentProvider,
  associatedClinicData,
) => {
  if (!currentProvider) return [];

  // Boomi returns address.street as an array of lines for some regions (e.g. APAC).
  // Handle both array and string forms so street1 is always a plain string.
  const street = currentProvider.address?.street;
  const street1 = Array.isArray(street)
    ? street[0] || ''
    : street || '';
  const street2 = Array.isArray(street) ? street[1] || '' : '';

  const requestingClinic = {
    clinicId: currentProvider.id || '',
    clinicName: currentProvider.name || '',
    addressId: currentProvider.address?.id || '',
    street1,
    street2,
    city: currentProvider.address?.city || '',
    state: currentProvider.address?.state || '',
    postcode: currentProvider.address?.postcode || '',
    country: currentProvider.countryCode || '',
    countryName: currentProvider.address?.country || '',
    billingAddress: currentProvider.billingAddress
      ? {
          id: currentProvider.billingAddress.id || null,
          street1: (() => {
            const s = currentProvider.billingAddress.street;
            return Array.isArray(s) ? s[0] || '' : s || '';
          })(),
          street2: (() => {
            const s = currentProvider.billingAddress.street;
            return Array.isArray(s) ? s[1] || '' : '';
          })(),
          city: currentProvider.billingAddress.city || null,
          state: currentProvider.billingAddress.state || null,
          postcode: currentProvider.billingAddress.postcode || null,
          country:
            currentProvider.billingAddress.countryCode ||
            currentProvider.countryCode ||
            '',
          countryName:
            currentProvider.billingAddress.country ||
            currentProvider.address?.country ||
            '',
        }
      : null,
    shippingAddress: currentProvider.shippingAddress
      ? {
          id: currentProvider.shippingAddress.id || null,
          street1: (() => {
            const s = currentProvider.shippingAddress.street;
            return Array.isArray(s) ? s[0] || '' : s || '';
          })(),
          street2: (() => {
            const s = currentProvider.shippingAddress.street;
            return Array.isArray(s) ? s[1] || '' : '';
          })(),
          city: currentProvider.shippingAddress.city || null,
          state: currentProvider.shippingAddress.state || null,
          postcode: currentProvider.shippingAddress.postcode || null,
          country:
            currentProvider.shippingAddress.countryCode ||
            currentProvider.countryCode ||
            '',
          countryName:
            currentProvider.shippingAddress.country ||
            currentProvider.address?.country ||
            '',
        }
      : null,
    isRequestingClinic: true,
  };

  // Map associated clinics, excluding the requesting clinic to avoid duplicates.
  const associatedClinics = (associatedClinicData?.values || [])
    .filter(
      (clinic) =>
        clinic.clinicId?.value !== requestingClinic.clinicId,
    )
    .map((clinic) => ({
      clinicId: clinic.clinicId?.value || '',
      clinicName: clinic.clinicName?.value || '',
      addressId: '',
      street1: clinic.street1?.value || '',
      street2: clinic.street2?.value || '',
      city: clinic.city?.value || '',
      state: clinic.state?.value || '',
      postcode: clinic.postcode?.value || '',
      country: clinic.country?.value || '',
      countryName: clinic.country?.value || '',
      isRequestingClinic: false,
    }));

  return [requestingClinic, ...associatedClinics];
};

/**
 * Transform device data to match SR Portal requirements
 */
export const formatDeviceDataForSR = (deviceData, deviceId) => {
  if (!deviceData) return null;

  const isUnknownEarSide =
    deviceData.isUnknownEarSIde ||
    deviceData.isUnknownEarSide ||
    false;

  // Derive earSide string when determinable
  let earSide;
  if (isUnknownEarSide) {
    earSide = 'Unknown';
  } else if (
    deviceData.earLeft !== undefined &&
    deviceData.earLeft !== null
  ) {
    earSide = deviceData.earLeft ? 'Left' : 'Right';
  }

  const formatted = {
    assetId: deviceData.assetId,
    deviceId: deviceData.deviceId || deviceId,
    productFamily: deviceData.productFamily,
    productSubFamily: deviceData.productSubFamily,
    startDate: deviceData.startDate,
    modelGroup: deviceData.modelGroup,
    earLeft: deviceData.earLeft,
    isUnknownEarSide,
    ...(earSide !== undefined && { earSide }),
    loaner: deviceData.loaner,
    ...(deviceData.smartSyncCompatibility !== undefined && {
      smartSyncCompatibility: deviceData.smartSyncCompatibility,
    }),
    ...(deviceData.smartSyncScenario !== undefined && {
      smartSyncScenario: deviceData.smartSyncScenario,
    }),
    device: deviceData.device
      ? {
          type: deviceData.device.type,
        }
      : null,
    model: deviceData.model
      ? {
          name: deviceData.model.name,
          info: deviceData.model.info,
          partNumber: deviceData.model.partNumber,
        }
      : null,
    warranty: deviceData.warranty
      ? {
          timestamp: deviceData.warranty.timestamp,
        }
      : null,
  };

  // Format associated accessories if present
  if (
    deviceData.associatedAccessories &&
    Array.isArray(deviceData.associatedAccessories)
  ) {
    formatted.associatedAccessories =
      deviceData.associatedAccessories.map((accessory) => ({
        assetId: accessory.assetId,
        deviceId: accessory.deviceId,
        productId: accessory.productId,
        productName: accessory.productName,
        productType: accessory.productType,
        productDescription: accessory.productDescription,
        productFamily: accessory.productFamily,
        productSubFamily: accessory.productSubFamily,
        partNumber: accessory.partNumber,
        serialNumber: accessory.serialNumber,
        lotNumber: accessory.lotNumber,
        earSide: accessory.earSide,
        status: accessory.status,
        startDate: accessory.startDate,
        endDate: accessory.endDate,
        isCurrent: accessory.isCurrent,
        warrantyExpiration: accessory.warrantyExpiration,
        warrantyStatus: accessory.warrantyStatus,
        isNewServiceRequestEnabled:
          accessory.isNewServiceRequestEnabled,
        isReportAnIssueEnabled: accessory.isReportAnIssueEnabled,
        isLoaner: accessory.isLoaner,
        serviceRequestId: accessory.serviceRequestId,
      }));
  }

  return formatted;
};
